import { modalController } from '@ionic/core';
import { Component, Element, Event, EventEmitter, forceUpdate, Fragment, h, Prop, State, Watch } from '@stencil/core';
import { DateUtils, FileDoc, DrawingPageDoc, DrawingDoc } from 'common';
import createPanZoom, { PanZoom } from 'panzoom';
import { FileService } from 'src/global/service/FileService';

@Component({
  tag: 'drawing-versions-modal',
  styleUrl: 'drawing-versions-modal.css',
  //shadow: true,
})
export class DrawingVersionsModal {
  static async present(projectId: string, files: FileDoc[], pages: DrawingPageDoc[]): Promise<DrawingPageDoc[]> {
    const modal = await modalController.create({
      component: 'drawing-versions-modal',
      cssClass : 'modal-full-view',
      componentProps: { projectId, files, pages },
      showBackdrop: true,
      backdropDismiss: false, 
    });
    
    return new Promise(async resolve => {
      modal.addEventListener('dismiss', (e: CustomEvent) => {
        resolve(e.detail as DrawingPageDoc[]);
        modal.dismiss();
      });
      modal.present();
    });
  }

  @Element() hostElement: HTMLElement;
  @Prop() projectId: string;
  @Prop() files: FileDoc[];
  @Prop() pages: DrawingPageDoc[];
  @Prop() pageId: string;

  @Event() dismiss: EventEmitter;
  @State() indexVersions: number = 0;
  private pageImages: string[] = [];

  private panzoomLeft: PanZoom;
  private panzoomRight: PanZoom;

  private leftImageRef: HTMLElement;
  private rightImageRef: HTMLElement;

  async componentWillLoad() {
    this.loadImage(this.selectedIndex);
    this.loadImage(this.otherVersionIndex);
  }

  async getPage(fileId: string, pageId: string) {
    const db = FileService.getFilePagesDb(this.projectId, fileId);
    const docs: DrawingPageDoc[] = await db.getDocs();
    return docs.find(page => page._id == pageId);
  }

  @Watch('indexVersions')
  onSelectedIndexChange() {
    this.loadImage(this.selectedIndex);
    this.loadImage(this.otherVersionIndex);
  }

  async loadImage(index: number) {
    if (!this.pageImages[index] && this.pages?.[index]) {
      const jpgBase64 = await FileService.getHighResJpgBase64(this.projectId, this.pages[index]);
      this.pageImages[index] = `data:image/jpeg;base64,${jpgBase64}`;;
      forceUpdate(this);
    }
  }

  async componentDidLoad() {
    const panzoomOptions = { maxZoom: 20, minZoom: 0.1, animate: true };
    this.panzoomLeft = createPanZoom(this.leftImageRef, panzoomOptions);
    this.panzoomRight = createPanZoom(this.rightImageRef, panzoomOptions);

    this.panzoomLeft.on('transform', () => {
      //this.panzoomRight.setTransformOrigin(this.panzoomLeft.getTransformOrigin());
      this.syncPanzoomRight();
    });

    // this.leftImageRef.addEventListener('wheel', (event) => this.panzoomLeft.zoomWithWheel(event), { passive: true });
  }

  get selectedIndex(): number {
    const current = this.pages.findIndex(p => p.current);
    if (current >= 0) {
      return current;
    } else {
      return FileService.getMostRecentPageIndex(this.files, this.pages);
    }
  }
  /**
   * Returns the currently selected page, e.g, the one that is shown on the left of the modal.
   * This page is considered the 'correct' version of the drawing.
   */
  get selected(): DrawingPageDoc {
    return this.pages[this.selectedIndex];
  }

  get selectedFile(): FileDoc {
    return this.files.find(f => f._id === this.selected.file_id);
  }

  get others(): DrawingPageDoc[] {
    return this.pages.filter(p => p !== this.selected);
  }

  /**
   * Returns the other versions of the selected page, e.g, the ones that are shown on the right of the modal.
   * These are considered the 'incorrect' versions of the drawing, e.g, older versions.
   */
  get otherVersion(): DrawingPageDoc {
    return this.others[this.indexVersions];
  }

  get otherVersionIndex(): number {
    return this.pages.indexOf(this.otherVersion);
  }

  get otherFile(): FileDoc {
    return this.files.find(f => f._id === this.otherVersion.file_id);
  }

  private async setAsCurrent() {
    // take a copy of the variables before modifying
    const sel = this.selected;
    const other = this.otherVersion;

    // switch the values of 'current' in the pages
    sel.current = false;
    other.current = true;

    // trigger an update so the render method is called
    forceUpdate(this);
  }

  private syncPanzoomRight() {
    const { scale, x, y } = this.panzoomLeft.getTransform();

    // We want the rigth side to move instantly with the left
    this.panzoomRight.zoomAbs(x, y, scale);
    this.panzoomRight.moveTo(x, y);
  }

  private zoomIn() {
    this.panzoomLeft.smoothZoom(this.leftImageRef.clientWidth / 2, this.leftImageRef.clientHeight / 2, 1.2);
  }

  private zoomOut() {
    this.panzoomLeft.smoothZoom(this.leftImageRef.clientWidth / 2, this.leftImageRef.clientHeight / 2, 0.8);
  }

  private setNextVersion() {
    this.indexVersions += 1;
  }

  private setPreviousVersion() {
    this.indexVersions -= 1;
  }

  async doDismiss() {
    this.dismiss.emit(this.pages);
  }

  render() {
    const imgLeftSrc = this.pageImages[this.selectedIndex] || 'loading';
    const imgRightSrc = this.pageImages[this.otherVersionIndex] || 'loading';
    return (
      <Fragment>        
        <modal-header modalTitle={`Compare Versions - File ${this.pages[0]?.drawing_num}`} onDismiss={() => this.dismiss.emit()}></modal-header>
        <ion-page class="ion-padding">
          <ion-grid>
            {/* Current Image */}
            <ion-row>
              <ion-col sizeMd="6" sizeXs="12">
                <ion-row>
                  <p> {DateUtils.formatIso(this.selectedFile.created_date)} </p>
                  <div class="image-container">
                    <img loading="lazy" ref={el => (this.leftImageRef = el as HTMLElement)} src={imgLeftSrc} class="image" alt="Current Version" />
                  </div>
                </ion-row>
              </ion-col>

              {/* Mobile buttons between images */}
              <ion-row class="buttons-between">
                <ion-col class="col-center">
                  <ion-button color="primary" size="small" onClick={() => this.zoomOut()}>
                    <ion-icon name="remove" />
                  </ion-button>
                  <ion-button color="primary" size="small" onClick={() => this.zoomIn()}>
                    <ion-icon name="add"></ion-icon>
                  </ion-button>
                </ion-col>
                <ion-col class="col-center">
                  <ion-button fill="clear" disabled={this.indexVersions == 0} onClick={() => this.setPreviousVersion()}>
                    <ion-icon name="arrow-back" />
                  </ion-button>
                  <ion-button fill="clear" disabled={this.indexVersions == this.others.length - 1} onClick={() => this.setNextVersion()}>
                    <ion-icon name="arrow-forward" />
                  </ion-button>
                </ion-col>
              </ion-row>

              {/* Other Versions Image */}
              <ion-col sizeMd="6" sizeXs="12">
                <ion-row>
                  <p> {DateUtils.formatIso(this.otherFile.created_date)} </p>
                  <div class="image-container right-image-container">
                    <img ref={el => (this.rightImageRef = el as HTMLElement)} src={imgRightSrc} class="image" alt="Previous Version" />
                  </div>
                </ion-row>
              </ion-col>
            </ion-row>

            {/*Buttons */}
            <ion-row class="desktop-buttons">
              <ion-col size="6" class="ion-align-self-center">
                <ion-button color="primary" onClick={() => this.zoomOut()}>
                  <ion-icon name="remove" />
                </ion-button>
                <ion-button color="primary" onClick={() => this.zoomIn()}>
                  <ion-icon name="add"></ion-icon>
                </ion-button>
              </ion-col>
              <ion-col class="ion-grid" size="6">
                <ion-col>
                  <ion-button fill="clear" disabled={this.indexVersions == 0} onClick={() => this.setPreviousVersion()}>
                    <ion-icon name="arrow-back" />
                  </ion-button>
                  <ion-button fill="clear" disabled={this.indexVersions == this.others.length - 1} onClick={() => this.setNextVersion()}>
                    <ion-icon name="arrow-forward" />
                  </ion-button>
                </ion-col>
                <ion-button color="primary" size="small" onClick={() => this.setAsCurrent()}>
                  Set as Current
                </ion-button>
              </ion-col>
            </ion-row>
          </ion-grid>
        </ion-page>
        <ion-footer class="ion-padding-horizontal">
          <ion-button slot="center" class="mobile-button" size="small" onClick={() => this.setAsCurrent()}>
            Set as Current
          </ion-button>
        </ion-footer>
      </Fragment>
    );
  }
}
